import { css } from '@emotion/react';
import type { FC } from 'react';
import { ImageGalleryComponent } from '~/_shared/components/imageGallery/imageGallery.component';
import { isSafari } from '~/_shared/utils/deviceDetect/deviceDetect.helpers';
import { noop } from '~/_shared/utils/function.helpers';
import { useTranslation } from '~/_shared/utils/hooks';
import type { useLocationImageGallery } from '~/customizeLocationPanel/useLocationImageGallery';
import type { Place } from '~/places/places.types';
import type { PlacesSearch } from '~/store/mapSettings/places/mapSettingsPlaces.state';
import { getPlacesToolCircleColors } from '../../mapTools/placesTool/placesTool.helpers';
import { placeCircleStyle } from '../../mapTools/placesTool/placesTool.styles';
import { SidebarContentContainer } from '../sidebarContent/sidebarContent.container';
import { SidebarTitleComponent } from '../sidebarTitle/sidebarTitle.component';

const descriptionWrapperStyle = css({
  padding: '22px 0',
});

const contentStyle = css({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  height: '100%',
});

const IMAGE_GALLERY_HEIGHT = 225;
const imageGalleryStyle = css({
  height: IMAGE_GALLERY_HEIGHT,
});

type PlaceDetailsComponentProps = Readonly<{
  placeSearch: PlacesSearch;
  placeIndex: number;
  place: Place;
  gallery?: ReturnType<typeof useLocationImageGallery>;
  onClose: () => void;
  onExpandClick?: () => void;
}>;

export const PlaceDetailsComponent: FC<PlaceDetailsComponentProps> = ({
  placeSearch,
  placeIndex,
  place,
  gallery,
  onClose,
  onExpandClick,
}) => {
  const [t] = useTranslation();

  return (
    <SidebarContentContainer
      description={(
        <SidebarTitleComponent
          onXClick={onClose}
          sidebarTitle={{
            header: place.name,
            description: [place.address],
          }}
          containerStyle={descriptionWrapperStyle}
          indicator={(
            <div
              css={placeCircleStyle(getPlacesToolCircleColors(placeSearch.color))}
            >
              {placeIndex + 1}
            </div>
          )}
        />
      )}
      isLoading={false}
      body={(
        <div css={contentStyle}>
          <div /> {/* additional info will go into this div in phase 2. Keeping here so styles don't need to be reworked */}
          <div>
            {gallery && (
              <ImageGalleryComponent
                items={gallery.galleryItems}
                renderItem={gallery.renderGalleryItem}
                css={imageGalleryStyle}
                onImageSelect={noop}
                onExpandClick={onExpandClick}
                triggerTooltip={t('Launch Street View')}
                allowOverflow={isSafari()}
              />
            )}
          </div>
        </div>
      )}
    />
  );
};
